import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    output,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../main/type.map.service";
import type { S25ItemI } from "../../pojo/S25ItemI";
import type { SearchCriteriaType } from "../../pojo/SearchCriteriaI";
import { SearchCriteriaContext } from "../../services/search/search-criteria/search.criteria.context";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { type MultiselectModelI } from "../s25-multiselect/s25.multiselect.component";
import { S25MultiselectSearchCriteriaComponent } from "../s25-multiselect/multiselects/s25.multiselect.search.criteria.component";
import { S25MultiselectModule } from "../s25-multiselect/s25-multiselect.module";
import { S25TriggerRerenderDirective } from "../s25-trigger-rerender/s25.trigger.rerender.directive";
import { S25TriggerRerenderModule } from "../s25-trigger-rerender/s25.trigger.rerender.module";

/** Selection plus the widget's current removal list, for the AngJS event PUT. */
export interface EventFormMultiselectChange {
    selectedItems: S25ItemI[];
    removedItems: S25ItemI[];
}

@TypeManagerDecorator("s25-ng-event-form-multiselect-item")
@Component({
    selector: "s25-ng-event-form-multiselect-item",
    template: `
        @if (!availableItems.isLoading()) {
            <s25-ng-multiselect-search-criteria
                *s25-ng-trigger-rerender="modelBean()"
                [type]="type()"
                [modelBean]="modelBean()"
                [popoverPlacement]="'bottom-left top-left auto'"
                [useSecurity]="true"
                [eventTypeId]="eventTypeId()"
                [selectedItems]="processedSelections()"
                [showResults]="true"
                [buttonText]="'Edit'"
                [ariaLabel]="'Edit ' + (display() || '')"
                [ariaRequired]="required()"
                [ariaInvalid]="ariaInvalid()"
                [ariaDescribedby]="ariaDescribedby()"
                (changed)="processedSelections.set($event)"
                (onDone)="processedSelections.set($event)"
            />
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [S25MultiselectModule, S25TriggerRerenderDirective],
})
export class MultiselectItemComponent {
    readonly type = input.required<SearchCriteriaType["type"]>();
    /** When true, prevent removing event_typed items in create mode */
    readonly lockEventTypeDefaults = input(false);
    readonly display = input("");
    readonly required = input(false);
    readonly formMode = input<"create" | "edit">("edit");
    readonly eventTypeId = input<number | undefined>(undefined);
    readonly ariaInvalid = input(false);
    readonly ariaDescribedby = input<string | undefined>(undefined);
    // TODO: use selectedItems = model() when we're out of angJS
    //  (CompileService does not support binding to model output)
    readonly selectedItems = input<S25ItemI[]>([]);
    readonly selectedItemsChange = output<S25ItemI[]>();

    readonly action = output<EventFormMultiselectChange>();
    readonly removeAction = output<EventFormMultiselectChange>();
    /**
     * Once we know whether there are any items to show, emit true/false. The parent
     * angJS component hides the element if we emit false.
     */
    readonly hasItems = output<boolean>();

    private readonly searchCriteriaContext = inject(SearchCriteriaContext);
    private readonly searchCriteria = viewChild(S25MultiselectSearchCriteriaComponent);

    readonly expensiveSearch = computed(() => !!this.searchCriteriaContext.Context[this.type()]?.expensiveSearch);

    /**
     * Available items for this user and event type
     */
    readonly availableItems = s25Resource({
        if: () => !this.expensiveSearch(),
        params: () => ({
            type: this.type(),
            eventTypeId: this.eventTypeId(),
        }),
        loader: ({ params }): Promise<S25ItemI[]> => {
            const context = this.searchCriteriaContext.Context[params.type];
            if (!context?.serviceMethod) {
                return Promise.resolve([]);
            }
            // empty customFilterValue to match how the child component queries serviceMethod.
            // otherwise, they have a slightly different @Cache key and we get two requests.
            return context.serviceMethod(true, params.eventTypeId, "");
        },
    });

    /** Event type defaults from the available list (empty until that list has loaded). */
    private readonly defaultItems = computed(() => {
        return this.availableItems.value()?.filter((available) => S25Util.toBool(available.event_typed)) ?? [];
    });

    /**
     * When in create mode, incoming selected items will have any event type defaults
     * appended to the selections list automatically.
     * TODO: consider separating defaults from selections so we can remove them when
     *  switching event types.
     */
    private readonly selectionWithDefaults = computed<S25ItemI[]>(() => {
        if (this.formMode() !== "create" || this.availableItems.value() === undefined) {
            // not appending any defaults
            return this.selectedItems() ?? [];
        }

        const selection = this.selectedItems();
        const selectedIds = selection?.map((item) => Number(item.itemId));
        const newItems = this.defaultItems()
            .filter((item) => !selectedIds?.includes(Number(item.itemId)))
            .map((item) => ({ ...item }));
        return [...selection, ...newItems];
    });

    readonly processedSelections = linkedSignal({
        source: () => this.selectionWithDefaults(),
        computation: (source, previous): S25ItemI[] => {
            // If the previous value is based on the same source, it may have edits,
            // so let's keep using it. This computation may just be re-running because
            // a different signal changed (e.g. lockEventTypeDefaults)
            const selection = previous?.source === source ? previous.value : source;
            this.lockPermanentItems(selection);
            return selection;
        },
    });

    readonly modelBean = computed((): MultiselectModelI => {
        this.eventTypeId();
        return {
            noSort: true,
            action: () => {
                // Popup Done. Re-checks update removedItems without firing removeAction.
                this.action.emit(this.selectionSnapshot());
            },
            removeAction: () => {
                // Chip remove does not emit changed/onDone — must set here.
                const snapshot = this.selectionSnapshot();
                this.processedSelections.set(snapshot.selectedItems);
                this.removeAction.emit(snapshot);
            },
        };
    });

    constructor() {
        effect(() => {
            if (!this.availableItems.isLoading()) {
                this.selectedItemsChange.emit(this.processedSelections());
            }
        });

        effect(() => {
            if (this.expensiveSearch()) {
                this.hasItems.emit(true);
            } else if (this.availableItems.value() !== undefined) {
                this.hasItems.emit(!!this.availableItems.value()?.length);
            }
        });

        // Search-criteria only reads selectedItems in ngOnInit — push AngJS/rule updates in.
        effect(() => {
            const items = this.processedSelections();
            this.modelBean().selectedItems = items;
            const child = this.searchCriteria();
            if (!child) {
                return;
            }
            child.value.set([...items]);
            child.refresh();
        });
    }

    /** Copies both lists so a later re-add cannot mutate the arrays AngJS already stored. */
    private selectionSnapshot(): EventFormMultiselectChange {
        return {
            selectedItems: [...(this.modelBean().selectedItems ?? [])],
            removedItems: [...(this.modelBean().removedItems ?? [])],
        };
    }

    /** Lock selections the user may not remove: unavailable items, and event type defaults on create. */
    private lockPermanentItems(selectedItems: S25ItemI[]): void {
        const availableItemIds = this.availableItems.value()?.map((item) => Number(item.itemId));
        const defaultItemIds = this.defaultItems().map((item) => Number(item.itemId));
        for (const selected of selectedItems) {
            if (availableItemIds && !availableItemIds.includes(Number(selected.itemId))) {
                selected.isPermanent = true;
            } else if (this.formMode() === "create" && defaultItemIds.includes(Number(selected.itemId))) {
                selected.isPermanent = this.lockEventTypeDefaults();
            } else {
                selected.isPermanent = false;
            }
        }
    }
}
